import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Banner from "../components/Banner";
import DropZone from "../components/DropZone";
import FileRow from "../components/FileRow";
import LinkButton from "../components/LinkButton";
import { RowSpinner } from "../components/Loader/Loader";
import PrimaryButton from "../components/PrimaryButton";
import { accountTitle } from "../components/syncunit/format";
import Toast from "../components/Toast";
import { ENABLE_SYNC_UNIT_CONTROLLER } from "../constants/constants";
import { SYNC_UNITS_PATH } from "../constants/syncRoutes";
import { createKeys, decryptKeys } from "../helpers/forgeEncryption";
import useNavigation from "../hooks/use-navigation";
import useToken from "../hooks/use-token";
import {
  useCreateEncryptionKeyMutation,
  useListUnsyncedBrokerReportsQuery,
  useDeleteFileMutation,
  useSynchronizedFilesQuery,
  useSynchronizeFilesMutation,
  useUploadBrokerReportMutation,
} from "../store";
import ReportInstructionsPage from "./ReportInstructionsPage";
import formatMissingFields from "./unifiedUpload/formatMissingFields";
import {
  actions,
  initialState,
  ROW,
  selectCanAddFiles,
  selectSyncGate,
  uploadPackageReducer,
} from "./unifiedUpload/uploadPackageReducer";
import useSubmissionQueue from "./unifiedUpload/useSubmissionQueue";

export const UNIFIED_UPLOAD_PATH = "/unified-upload";
export const REPORT_INSTRUCTIONS_PATH = "/unified-upload/instructions";
export const UNIFIED_ACCOUNTS_PATH = "/unified-upload/accounts";
export const UNIFIED_SYNC_CACHE_KEY = "unifiedSync";

export function isUnifiedUploadPath(path) {
  return path === UNIFIED_UPLOAD_PATH || path === REPORT_INSTRUCTIONS_PATH || path === UNIFIED_ACCOUNTS_PATH;
}

// LocalDateTime "2025-01-01T00:00:00" → "01.01.2025", without a time-zone round trip.
function formatDate(localDateTime) {
  const [year, month, day] = (localDateTime || "").slice(0, 10).split("-");
  return day ? `${day}.${month}.${year}` : "";
}

function reportMeta(t, report) {
  return t("unifiedUpload.meta", {
    provider: t(`unifiedUpload.providers.${report.dataProvider}`, { defaultValue: report.dataProvider }),
    start: formatDate(report.periodStart),
    end: formatDate(report.periodEnd),
  });
}

// The deferred reports by file name and the accounts they belong to (screens.md §SCR-01 deferred states).
export function deferredSummary(deferredIds, rows) {
  if (deferredIds.length === 0) return null;
  const reports = deferredIds.map((id) => rows.find((row) => row.report?.id === id)?.report).filter(Boolean);
  const unique = (values) => [...new Set(values.filter(Boolean))].join(", ");
  return {
    count: deferredIds.length,
    names: unique(reports.map((report) => report.fileName)),
    accounts: unique(reports.map((report) => accountTitle(report))),
  };
}

function invalidMessage(t, error) {
  const { formatLabel, items } = formatMissingFields(t, error.details, error.commonMessage);
  if (error.code === "broker_report.structurally_invalid") {
    return t("unifiedUpload.invalidMissing", { format: formatLabel, list: items.join(", ") });
  }
  return t("unifiedUpload.invalidPeriod", { format: formatLabel, message: error.commonMessage || "" }).trim();
}

const MAX_NAMED_BLOCKERS = 5;

function blockerNames(t, names) {
  if (names.length <= MAX_NAMED_BLOCKERS) return names.join(", ");
  return t("unifiedUpload.sync.andMore", {
    names: names.slice(0, MAX_NAMED_BLOCKERS).join(", "),
    count: names.length - MAX_NAMED_BLOCKERS,
  });
}

function ListStatus({ list, onRetry }) {
  const { t } = useTranslation();
  if (list === "loading") {
    return (
      <div className="mt-4 flex items-start gap-3 rounded-2xl bg-white border border-[#e2e2e2] px-4 py-3">
        <RowSpinner />
        <div>
          <p className="text-sm">{t("unifiedUpload.list.loading")}</p>
          <p className="text-xs text-[#8c8c8c]">{t("unifiedUpload.list.loadingHint")}</p>
        </div>
      </div>
    );
  }
  if (list === "error") {
    return (
      <div className="mt-4 rounded-2xl bg-white border border-[#f0c2c2] px-4 py-3">
        <p className="text-sm text-[#c23b3b]">{t("unifiedUpload.list.error")}</p>
        <p className="text-xs text-[#8c8c8c]">{t("unifiedUpload.list.errorHint")}</p>
        <LinkButton onClick={onRetry} className="mt-2">
          {t("unifiedUpload.list.retry")}
        </LinkButton>
      </div>
    );
  }
  return null;
}

function UnifiedUploadPage() {
  const { t } = useTranslation();
  const { currentPath, navigate, locationState, clearLocationState } = useNavigation();
  // broker-report-sync: «Додати звіт» on SCR-03 hands the account over; the mark only informs (the server still
  // splits reports by account + data provider, AC-01) and is gone after a reload.
  const [addToAccount, setAddToAccount] = useState(() => (ENABLE_SYNC_UNIT_CONTROLLER && locationState?.addToAccount) || null);
  useEffect(() => {
    if (ENABLE_SYNC_UNIT_CONTROLLER && locationState?.addToAccount) {
      setAddToAccount(locationState.addToAccount);
      clearLocationState?.();
    }
  }, [locationState, clearLocationState]);
  const [allDeferred, setAllDeferred] = useState(null);
  const token = useToken();
  const [state, dispatch] = useReducer(uploadPackageReducer, initialState);
  const [createEncryptionKey] = useCreateEncryptionKeyMutation();
  const [uploadBrokerReport] = useUploadBrokerReportMutation();
  const [deleteReport] = useDeleteFileMutation();
  const [synchronize, { isLoading: syncing }] = useSynchronizeFilesMutation({ fixedCacheKey: UNIFIED_SYNC_CACHE_KEY });
  const [syncNotFound, setSyncNotFound] = useState(false);
  const [syncNotStarted, setSyncNotStarted] = useState(false);
  const [toast, setToast] = useState(null);

  // AC-12: the own unsynced reports are read on every entry; later reads only add reports —
  // except the read right after a 404 (sad §6 Flows 2 and 5), which is authoritative: rows it no longer
  // has disappear. Removal (DELETE) and /investing-accounts/synchronize invalidate the list tag, so that read is already on its way.
  const listQuery = useListUnsyncedBrokerReportsQuery(undefined, { skip: !token, refetchOnMountOrArgChange: true });
  const { data: serverReports, isError: listFailed, isFetching: listFetching, refetch: refetchList } = listQuery;
  const replaceNextList = useRef(false);

  // The sidebar's arguments, so both share one cache entry. /reports without synced accounts bounces back here,
  // so the link waits for a non-empty answer (hidden while loading and on an error).
  const { data: synchronized } = useSynchronizedFilesQuery({ dataProvider: "", page: 0, size: 1 });
  const hasSynchronized = synchronized?.content?.length > 0;

  useEffect(() => {
    if (!serverReports) return;
    dispatch(replaceNextList.current ? actions.serverListReplaced(serverReports) : actions.serverReportsLoaded(serverReports));
    replaceNextList.current = false;
  }, [serverReports]);

  useEffect(() => {
    if (listFailed && !listFetching) dispatch(actions.serverListFailed());
  }, [listFailed, listFetching]);

  const retryList = () => {
    dispatch(actions.serverListRequested());
    refetchList();
  };

  const reloadList = useCallback(() => {
    replaceNextList.current = true;
    refetchList();
  }, [refetchList]);

  const removeReport = async (reportId) => {
    dispatch(actions.removeStarted(reportId));
    try {
      await deleteReport({ id: reportId }).unwrap();
      dispatch(actions.reportRemoved(reportId));
      setToast(t("unifiedUpload.reportRemovedToast"));
    } catch (error) {
      if (error?.status === 409 && error?.data?.code === "broker_report.still_synchronizing") {
        dispatch(actions.removeConflict(reportId));
      } else {
        dispatch(actions.removeAborted(reportId));
        if (error?.status === 404) reloadList();
      }
    }
  };

  // Flow 1: one RSA pair and one single-use key per file — a retry runs the whole chain again.
  const submitFile = useCallback(
    async (row) => {
      const { publicKey, privateKey } = await createKeys();
      const key = await createEncryptionKey({ key: publicKey }).unwrap();
      const { encryptedFile } = await decryptKeys(key, privateKey, row.file);
      return uploadBrokerReport({ encryptionId: key.id, file: encryptedFile, fileName: row.fileName }).unwrap();
    },
    [createEncryptionKey, uploadBrokerReport],
  );

  useSubmissionQueue(state.rows, dispatch, submitFile);

  const showInstructions = currentPath === REPORT_INSTRUCTIONS_PATH;
  const atLimit = !selectCanAddFiles(state);
  const gate = selectSyncGate(state);

  // AC-09: the request is never sent while the gate is not ready.
  const startSync = async () => {
    if (gate.kind !== "ready" || syncing) return;
    setSyncNotFound(false);
    setSyncNotStarted(false);
    setAllDeferred(null);
    try {
      const response = await synchronize({ ids: gate.readyReportIds }).unwrap();
      if (!ENABLE_SYNC_UNIT_CONTROLLER) {
        // The 200 response stays in the mutation cache (fixedCacheKey) for SCR-03.
        navigate(UNIFIED_ACCOUNTS_PATH);
        return;
      }
      // Reports of an account whose SyncUnit is SYNCING / FAILED stay accepted here (Flow US-07, D13).
      const deferred = deferredSummary(response?.deferredBrokerReportIds || [], state.rows);
      if (!deferred) navigate(SYNC_UNITS_PATH);
      else if (deferred.count === gate.readyReportIds.length) setAllDeferred(deferred);
      else navigate(SYNC_UNITS_PATH, { state: { deferred } });
    } catch (error) {
      if (error?.status === 404) {
        setSyncNotFound(true);
        reloadList();
      } else {
        setSyncNotStarted(true);
      }
    }
  };

  const hintKind = syncing ? "syncing" : gate.kind;
  const hintCount = {
    processing: gate.pendingCount,
    blocked: gate.blockers.length,
    ready: gate.readyReportIds.length,
    syncing: gate.readyReportIds.length,
  }[hintKind];

  // SCR-02 is a nested view of SCR-01: the submission stays mounted (hidden) so the
  // upload package survives the trip to the instructions and back.
  return (
    <>
      <div className="page" hidden={showInstructions}>
        <h1 className="text-2xl font-semibold tracking-tight">{t("unifiedUpload.title")}</h1>
        <p className="text-sm mt-2 max-w-[560px] text-[#8c8c8c]">{t("unifiedUpload.subtitle")}</p>
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
          <LinkButton to={REPORT_INSTRUCTIONS_PATH}>{t("unifiedUpload.howToGetReport")}</LinkButton>
          {/* Always shown: the only way back to DRAFT accounts once the investor has left SCR-03. */}
          <LinkButton to={ENABLE_SYNC_UNIT_CONTROLLER ? SYNC_UNITS_PATH : UNIFIED_ACCOUNTS_PATH}>
            {t("unifiedUpload.links.syncingAccounts")}
          </LinkButton>
          {hasSynchronized && <LinkButton to="/reports">{t("unifiedUpload.links.synchronizedAccounts")}</LinkButton>}
        </div>

        {addToAccount && (
          <Banner tone="warn" className="mt-6">
            <p>{t("unifiedUpload.addToAccount.text", { account: addToAccount.title })}</p>
            <LinkButton onClick={() => setAddToAccount(null)}>{t("unifiedUpload.addToAccount.cancel")}</LinkButton>
          </Banner>
        )}

        <div className="mt-6">
          <DropZone
            atLimit={atLimit}
            onFiles={(files) => dispatch(actions.filesSelected(files))}
            title={t(atLimit ? "unifiedUpload.dropZone.atLimitTitle" : "unifiedUpload.dropZone.title")}
            caption={t(atLimit ? "unifiedUpload.dropZone.atLimitCaption" : "unifiedUpload.dropZone.caption")}
          />
        </div>

        {state.limitError && (
          <Banner tone="danger" className="mt-4">
            {t("unifiedUpload.limitBanner")}
          </Banner>
        )}

        {state.rows.length > 0 && (
          <ul className="mt-4 flex flex-col gap-2">
            {state.rows.map((row) => (
              <FileRow
                key={row.clientId}
                fileName={row.fileName}
                state={row.state}
                queued={row.state === ROW.PROCESSING && !row.active}
                meta={row.report ? reportMeta(t, row.report) : undefined}
                message={row.state === ROW.INVALID ? invalidMessage(t, row.error) : undefined}
                removingReport={row.removingReport}
                removeReportBlocked={row.removeReportBlocked}
                disabled={syncing}
                onRemoveReport={() => removeReport(row.report.id)}
                onRemove={() => dispatch(actions.rowRemoved(row.clientId))}
                onRetry={() => dispatch(actions.retry(row.clientId))}
              />
            ))}
          </ul>
        )}

        <ListStatus list={state.list} onRetry={retryList} />

        {gate.kind === "blocked" && (
          <Banner tone="danger" className="mt-6">
            {t("unifiedUpload.sync.blockedBanner", { names: blockerNames(t, gate.blockers) })}
          </Banner>
        )}
        {syncNotFound && (
          <Banner tone="danger" className="mt-6">
            {t("unifiedUpload.sync.notFoundBanner")}
          </Banner>
        )}

        {syncNotStarted && (
          <Banner tone="danger" className="mt-6">
            {t("unifiedUpload.sync.notStartedBanner")}
          </Banner>
        )}

        {allDeferred && (
          <Banner tone="warn" className="mt-6">
            {t("unifiedUpload.deferred.allDeferred", allDeferred)}
          </Banner>
        )}

        <div className="mt-4 flex flex-col md:flex-row md:items-center gap-3">
          <PrimaryButton onClick={startSync} disabled={gate.kind !== "ready"} loading={syncing} className="w-full md:w-auto">
            {t(syncing ? "unifiedUpload.sync.syncing" : "unifiedUpload.sync.button")}
          </PrimaryButton>
          <p className={gate.kind === "blocked" && !syncing ? "text-sm text-[#c23b3b]" : "text-sm text-[#8c8c8c]"}>
            {t(`unifiedUpload.sync.hint.${hintKind}`, { count: hintCount })}
          </p>
        </div>
      </div>
      {toast && <Toast message={toast} type="info" onClose={() => setToast(null)} />}
      {showInstructions && <ReportInstructionsPage />}
    </>
  );
}

export default UnifiedUploadPage;
